import type { ActiveShift, DesktopSales, ShiftTakings } from '@repo/desktop-bridge';
import { Alert, AlertDescription, AlertTitle } from '@repo/ui/components/alert';
import { Button } from '@repo/ui/components/button';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useContext, useEffect, useState } from 'react';

import { formatPaise } from '@/lib/money';

import { PAYMENT_MODES } from './payment-modes';
import { SharedDrawer } from './shared-drawer';
import { TillActions, tillPlate } from './TillFrame';

/**
 * What this shift took, shown before the shift ends.
 *
 * **A drawer that cannot be counted is a shortfall found the next day, in the
 * back office, by somebody who was not there.** Until this screen `End shift`
 * ended the shift and said nothing, so the person who filled the drawer — the
 * only person who can still explain a difference — had no figure to count
 * against. That is the whole of why the count comes before the ending rather
 * than after it, and why ending is confirmed from here rather than from the
 * button that opened it.
 *
 * ## What it is careful not to claim
 *
 * **This is what this till recorded, and not what the office has agreed to.**
 * The server owns whether a sale is refunded, which business day it lands in and
 * whether that day has closed; a terminal that has been offline since lunch
 * knows none of it. So nothing here is worded as a reconciliation, no figure is
 * called "expected", and the sentence at the foot says plainly whose numbers
 * these are. Day Close is a different milestone and it is the one that agrees
 * the two.
 *
 * It is also **this till's** drawer and not the outlet's. Two tills at one
 * counter each hold their own, and adding them up needs the server.
 *
 * ## One drawer, several outlets (#254)
 *
 * A counter that sells for several outlets keeps a shift per outlet and one
 * physical drawer for all of them — the owner accepted that. This screen counts
 * the outlet on the plate and nothing else, because Day Close agrees each
 * outlet's day on its own; so where the drawer is shared it says so before the
 * figures, and says what the drawer is to be counted against instead.
 *
 * ## Refunds are subtracted, and they have to be
 *
 * A sale takes ₹500 in cash and a refund hands ₹500 back out of the same drawer.
 * A count that showed only what was taken would be over by every refund of the
 * day and would read as a cashier who was up on the till — which is exactly the
 * accusation this screen exists to prevent. So the refunds this shift queued are
 * counted against it, whenever the sales themselves were rung up: the money came
 * out of the drawer being counted.
 *
 * ## The float is deliberately not asked for
 *
 * A till opens with change in it and nothing in this system records how much.
 * Asking for it here would produce a figure a cashier typed and nothing checked,
 * on a screen whose one virtue is that every number on it came from a recorded
 * sale. So the wording is "took" and "handed back", never "should be in the
 * drawer" — the person counting adds their own opening float, which they know
 * and this device does not.
 */

/**
 * One amount in the split, set to be compared down its column.
 *
 * Tabular figures, right-aligned: Geist's proportional figures make eight `1`s
 * measure half what eight `0`s do, and comparing down a column is the entire
 * task at a cash-up (`docs/ui-conventions.md`).
 */
const COLUMN = 'text-right font-mono text-xl leading-none text-foreground numeric';

export function EndShift({
  sales,
  shift,
  onBack,
  onEnd,
}: {
  /** The bridge slice that answers `shift()`. Never `null` here — see `SellGoods`. */
  sales: DesktopSales;
  shift: ActiveShift;
  /** Back to the counter, with the shift still running. */
  onBack: () => void;
  /** End the shift for real. The screen goes with it. */
  onEnd: () => void;
}) {
  /**
   * `'unknown'` until the device answers, `null` when there is no count.
   *
   * The bridge answers `null` for "nobody is at this till", which this screen
   * cannot be opened without — so in practice `null` here is a failed read, and
   * the two are deliberately given one answer. Separating them would mean
   * writing a sentence about a shift that ended for somebody who is standing in
   * front of a shift that has not.
   */
  const [takings, setTakings] = useState<ShiftTakings | null | 'unknown'>('unknown');

  useEffect(() => {
    let live = true;

    sales
      .shift()
      .then((answer) => {
        if (live) setTakings(answer);
      })
      .catch((error: unknown) => {
        // Said on screen rather than swallowed, and **the shift can still be
        // ended**. A cashier who cannot get a count still has to go home, and a
        // screen that trapped them there over a failed read would be worse than
        // the silence it replaced.
        console.error('till: the shift takings could not be read', error);
        if (live) setTakings(null);
      });

    return () => {
      live = false;
    };
  }, [sales]);

  const refunded = takings !== null && takings !== 'unknown' && takings.refunds.count > 0;

  /** The outlet being counted, where the drawer holds other outlets' cash too. */
  const outlet = useContext(SharedDrawer);

  return (
    // The same fixed field every till screen sits in: a head that does not move,
    // a body that takes what is left and scrolls inside itself, and the two
    // buttons at a constant distance from the bottom of the counter. `min-h-0`
    // is what lets the middle shrink instead of pushing them off the screen.
    <div className="mx-auto flex h-full w-full max-w-[52rem] flex-col gap-5">
      {/* **The title band is gone — Move 1 of issue #206.** The name goes beside
          the mark, because this screen is pushed over the counter and a column
          of figures with nothing naming it is a column of figures.

          `{shift.displayName} · {shift.staffCode}` went with it and is not
          replaced: the frame's own footer already states who is at this till,
          and the button at the foot of this screen says the name again in the
          sentence it is about to act on. Three copies of one fact was two too
          many. The two controls stay where they are — they are what this screen
          is *for*, not chrome around it, and the till rule is that the thing
          which commits sits at a fixed distance from the bottom of the counter. */}
      <TillActions label="End shift">
        <Button variant="outline" size="touch" onClick={onBack}>
          Back to the counter
        </Button>
      </TillActions>

      {outlet !== undefined && (
        // Before the figures, so nobody counts the whole drawer against one
        // outlet's share of it and reads the rest as a surplus.
        <p className="shrink-0 rounded-2xl bg-muted px-4 py-3 text-sm text-foreground">
          <span className="font-semibold">These are {outlet}&rsquo;s takings only.</span> The drawer
          also holds what this till took for its other outlets. End the shift at each, then count
          the drawer against all their figures together.
        </p>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto">
        {takings === 'unknown' ? (
          <CountSkeleton />
        ) : takings === null ? (
          <Alert variant="destructive">
            <IconAlertTriangle />
            <AlertTitle>This till could not count the shift</AlertTitle>
            <AlertDescription>
              Nothing is wrong with the sales — they are recorded and they will reach the office.
              What failed is the count on this screen. Note what is in the drawer, end the shift,
              and report this.
            </AlertDescription>
          </Alert>
        ) : (
          <Count takings={takings} refunded={refunded} />
        )}
      </div>

      <div className="flex shrink-0 flex-col gap-3">
        {/* The primary action, and the only one on this screen that does
            anything. Not `destructive`: ending a shift is not irreversible —
            the same person signs back in with the same PIN — and this system
            spends red on the things that cannot be undone.

            It has the foot to itself now. *Back to the counter* was beside it as
            a `sm:flex-1` half, which made the way out and the thing that ends
            the shift two buttons of one size at one place — a 50/50 choice at
            the exact moment somebody is tired and reaching without looking. The
            way back is chrome and is with the rest of it, beside the mark. */}
        <Button size="touch" onClick={onEnd}>
          {outlet === undefined
            ? `End shift, ${shift.displayName}`
            : `End shift at ${outlet}, ${shift.displayName}`}
        </Button>
      </div>
    </div>
  );
}

/**
 * The figures, at the scale they are read at.
 *
 * The total is the largest thing on the screen for the reason `Take payment`'s
 * is: it is the number that gets read aloud and counted against, and a cashier
 * standing back from a counter has to be able to see it. Tabular figures down
 * every column, because comparing down a column is the entire task
 * (`docs/ui-conventions.md`).
 */
function Count({ takings, refunded }: { takings: ShiftTakings; refunded: boolean }) {
  const { sales, refunds, net } = takings;

  return (
    <div className="space-y-5">
      <section className="rounded-2xl border border-border bg-card px-5 py-4">
        <div className="flex items-baseline justify-between gap-4">
          {/* The headline figure is **net**, because the drawer is. Said on the
              label rather than left to be inferred from the table below it: a
              cashier reading one number off this screen must not read a gross
              total as the thing to count against. */}
          <span className={tillPlate}>
            {sales.count} {sales.count === 1 ? 'sale' : 'sales'}
            {refunded && ' · after refunds'}
          </span>
          <span className="font-mono text-5xl leading-none font-medium text-foreground numeric">
            {formatPaise(net.totalPaise)}
          </span>
        </div>

        {/* The datum, doing the datum's own job: separating what was counted
            from what it comes to. The same 3px rule the basket's foot uses. */}
        <div className="mt-4 h-[3px] w-full datum bg-foreground" />

        {/* Three rows and one, two or three columns. The refund columns are
            absent rather than zeroed on the ordinary day — a column of `₹0.00`
            beside every line teaches somebody to stop reading the row that
            matters. */}
        <table className="mt-4 w-full">
          <thead>
            <tr>
              <th scope="col" className={`${tillPlate} text-left`}>
                <span className="sr-only">How they paid</span>
              </th>
              <th scope="col" className={`${tillPlate} text-right`}>
                Took
              </th>
              {refunded && (
                <>
                  <th scope="col" className={`${tillPlate} text-right`}>
                    Gave back
                  </th>
                  <th scope="col" className={`${tillPlate} text-right`}>
                    Net
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {PAYMENT_MODES.map(({ mode, label }) => (
              <tr key={mode}>
                <th scope="row" className="py-3 text-left text-base font-medium text-foreground">
                  {label}
                </th>
                <td className={`py-3 ${COLUMN}`}>{formatPaise(sales.byMode[mode])}</td>
                {refunded && (
                  <>
                    <td className={`py-3 ${COLUMN}`}>
                      {refunds.byMode[mode] === 0 ? (
                        // A dash rather than `₹0.00`: nothing came back on this
                        // mode, and a zero amount reads as a figure somebody
                        // has to account for.
                        <span aria-label="nothing">—</span>
                      ) : (
                        `−${formatPaise(refunds.byMode[mode])}`
                      )}
                    </td>
                    <td className={`py-3 ${COLUMN} font-medium`}>
                      {formatPaise(net.byMode[mode])}
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {refunded && (
        // Said as a sentence as well as shown as a column, because it is the one
        // figure a drawer count goes wrong on: the money left the drawer and no
        // sale on this screen accounts for it.
        //
        // `text-foreground` on `bg-muted` — the muted pairing misses AA and is
        // the one a quiet inset panel invites (`docs/ui-conventions.md`).
        <p className="rounded-2xl bg-muted px-4 py-3 text-sm text-foreground">
          {refunds.count} {refunds.count === 1 ? 'refund was' : 'refunds were'} handed back on this
          shift, <span className="font-mono numeric">{formatPaise(refunds.totalPaise)}</span> in
          all. That money has already left the drawer, so the figures above are net of it.
        </p>
      )}

      {/* **The sentence this whole screen turns on.** The device's log is not
          the record, and a count worded as though the office had agreed to it
          would be the till claiming an authority it does not have. It also names
          the float, which nothing here knows. */}
      <p className="text-sm text-muted-foreground">
        This is what this till recorded, on its own clock. Count the drawer against it, remembering
        the change it opened with. The office&rsquo;s own figures are the ones that settle the day.
      </p>
    </div>
  );
}

/** A skeleton in the shape of the count, so nothing jumps when it lands. */
function CountSkeleton() {
  return (
    <div
      className="space-y-5 rounded-2xl border border-border bg-card px-5 py-4"
      aria-hidden
      data-testid="till-shift-count-skeleton"
    >
      <div className="h-12 w-2/3 animate-pulse rounded-xl bg-muted" />
      <div className="h-[3px] w-full bg-muted" />
      {[0, 1, 2].map((slot) => (
        <div key={slot} className="h-8 w-full animate-pulse rounded-lg bg-muted" />
      ))}
    </div>
  );
}
